import React from 'react';
import _ from 'lodash';

import {
  errorUtil,
  fields,
  validationUtil
} from 'iron-forge-react-modules';
import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Stack,
  styled,
  ToggleButton,
  ToggleButtonGroup as MuiToggleButtonGroup,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  GridProps
} from '@mui/material';
import {
  appFormUtil,
  FormModal,
  selectFields
} from 'modules';
import {
  Controller,
  useFieldArray,
  useForm,
  useWatch
} from 'react-hook-form';

import { StoreTypeName } from 'generated/graphql';
import { DeleteForever } from '@mui/icons-material';
import * as types from './types';
import * as dataService from './dataService';



// const {
//   genController: genControlledShipStationStore
// } = selectFields.ShipStationStoreSelectField.genComponentAndControllerGenerator();
// const ControlledShipStationStoreSelectField = genControlledShipStationStore<types.IFormState>();
const {
  genController: genControlledSalesChannelSelectField
} = selectFields.SalesChannelSelectField.genComponentAndControllerGenerator();
const ControlledSalesChannelSelectField = genControlledSalesChannelSelectField<types.IFormState>();
const {
  genController: genControlledNetSuiteBrandSelectField
} = selectFields.NetSuiteBrandSelectField.genComponentAndControllerGenerator();
const ControlledNetSuiteBrandSelectField = genControlledNetSuiteBrandSelectField<types.IFormState>();
const {
  genController: genControlledNetSuiteLocationSelectField
} = selectFields.NetSuiteLocationSelectField.genComponentAndControllerGenerator();
const ControlledNetSuiteLocationSelectField = genControlledNetSuiteLocationSelectField<types.IFormState>();
const {
  genController: genControlledNetSuiteSubsidiarySelectField
} = selectFields.NetSuiteSubsidiarySelectField.genComponentAndControllerGenerator();
const ControlledNetSuiteSubsidiarySelectField = genControlledNetSuiteSubsidiarySelectField<types.IFormState>();
const {
  genController: genControlledNetSuiteDepartmentSelectField
} = selectFields.NetSuiteDepartmentSelectField.genComponentAndControllerGenerator();
const ControlledNetSuiteDepartmentSelectField = genControlledNetSuiteDepartmentSelectField<types.IFormState>();
const {
  genController: genControlledNetSuiteTaxItemSelectField
} = selectFields.NetSuiteTaxItemSelectField.genComponentAndControllerGenerator();
const ControlledNetSuiteTaxItemSelectField = genControlledNetSuiteTaxItemSelectField<types.IFormState>();
const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();

const ToggleButtonGroup = styled(MuiToggleButtonGroup)(({ theme }) => ({
  marginBottom: theme.spacing(3)
}));

const ContainerBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2)
}));

const EnableWorkflowBox = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  background: '#FAFAFA',
  border: '1px solid #F7F7F8',
  borderRadius: 6,
  padding: '6px 12px',
  color: '#D8D8D8'
});



export interface IProps {
  onClose: () => void;
}

export const FC: React.FC<IProps> = props => {
  const form = useForm<types.IFormState>({
    defaultValues: {
      name: '',
      websiteUrl: '',
      orderPrefix: '',
      avaTaxCompanyCode: '',
      baseUrl: '',
      wooCommerceKey: '',
      wooCommerceSecret: '',
      shopifyAdminKey: '',
      telgoo5CustomerId: '',
      telgoo5PartnerName: '',
      telgoo5SourceUrl: '',
      telgoo5ApiUrl: '',
      clientIdentifiers: [],

      // SFTP Server fields
      sftpHost: '',
      sftpUsername: '',
      sftpPassword: '',
      sftpOrderFileDirectory: '',
      sftpAckFileDirectory: '',
      sftpShipFileDirectory: '',

      e1Branch: '',
      e1CompanyCode: '',
      e1DocType: '',
      e1ShipTo: '',
      e1SoldTo: '',
      e1TransactionCode: '',

      netSuiteClientInternalId: '',

      receivingWorkflow: {
        binId: '',
        enabled: 'true',
        name: ''
      },
      triageWorkflow: {
        binId: '',
        enabled: 'true',
        name: ''
      },
      replacementWorkflow: {
        binId: '',
        enabled: 'false',
        name: ''
      },
      repairWorkflow: {
        binId: '',
        enabled: 'false',
        name: ''
      },
      returnWorkflow: {
        binId: '',
        enabled: 'false',
        name: ''
      },
      creditWorkflow: {
        binId: '',
        enabled: 'false',
        name: ''
      },
      scrapWorkflow: {
        binId: '',
        enabled: 'false',
        name: ''
      }
    }
  });
  const fieldArray = useFieldArray<types.IFormState>({
    control: form.control,
    name: 'clientIdentifiers'
  });
  const [saveStore, saveStoreTuple] = dataService.useSaveStore();
  const [storeType, setStoreType] = React.useState<StoreTypeName>('wooCommerce');
  const formWatch = useWatch({ control: form.control });



  React.useEffect(
    () => {
      if (storeType === 'wooCommerce' || storeType === 'wooCommerce_buyWithAmazon') {
        form.setValue('e1TransactionCode', 'WOOCOM');
      } else if (storeType === 'customizableFormat') {
        form.setValue('e1TransactionCode', '3PL');
      } else if (storeType === 'shopify') {
        form.setValue('e1TransactionCode', 'SHOPIFY');
      } else {
        form.setValue('e1TransactionCode', '');
      }
    },
    [
      form,
      storeType
    ]
  );



  function handleChangeStoreType(
    event: React.MouseEvent<HTMLElement>,
    newAlignment: string | null
  ) {
    if (newAlignment) {
      setStoreType(newAlignment as StoreTypeName);
    }
  }



  const handleClickAddIpAddress = React.useCallback(() => {
    fieldArray.append({
      type: 'ipAddress',
      value: ""
    });
  }, [fieldArray]);

  const handleClickDeleteIpAddress = React.useCallback((index: number) => {
    fieldArray.remove(index);
  }, [fieldArray]);



  const processSubmit = React.useCallback(
    async (formState: types.IFormState) => {
      await errorUtil.errorWrap(async () => {
        await saveStore(appFormUtil.trimFormStateValues(formState), storeType);
        props.onClose();
      }, {
        INVALID_ARGS: "There was an error with the data passed to the server.",
        E1_META_MISSING: "E1 data must be provided when creating a store.",
        CANNOT_SPECIFY_E1_META_AND_PARTNER: "Cannot specify both E1 meta data and partner information.",
        MUST_SPECIFY_TRIAGE_WORKFLOWS_FOR_CLEO_STORE: "When creating a Cleo T1 store, must also specify triage workflows.",
        CANNOT_ADD_TRIAGE_WORKFLOWS_FOR_NON_CLEO_STORE: "Cannot add triage workflows for non-Cleo T1 stores."
      });
    },
    [
      props,
      saveStore,
      storeType
    ]
  );

  const defaultGridProps: GridProps = {
    item: true,
    xs: 12
  };

  const defaultThirdGridProps: GridProps = {
    ...defaultGridProps,
    md: 4
  };

  const defaultHalfGridProps: GridProps = {
    ...defaultGridProps,
    md: 6
  };

  const defaultFullGridProps: GridProps = {
    ...defaultGridProps,
    md: 12
  };

  const defaultTextFieldProps = {
    disableDefaultMargins: true,
    isRequired: true,
    isDisabled: saveStoreTuple.loading,
    formControl: form.control
  };

  return (
    <FormModal.FC
      title="Create Store"
      isDoubleWide
      isLoading={saveStoreTuple.loading}
      onSubmit={form.handleSubmit(processSubmit)}
      onClose={props.onClose}>
      <ContainerBox>
        <Grid
          container
          spacing={2}>
          <Grid {...defaultFullGridProps}>
            <ControlledTextField
              {...defaultTextFieldProps}
              fieldKey="name"
              label="Name" />
          </Grid>

          <Grid {...defaultThirdGridProps}>
            <ControlledTextField
              {...defaultTextFieldProps}
              fieldKey="orderPrefix"
              label="Order Prefix" />
          </Grid>

          <Grid {...defaultThirdGridProps}>
            <ControlledTextField
              {...defaultTextFieldProps}
              fieldKey="avaTaxCompanyCode"
              label="AvaTax Company Code"
            />
          </Grid>

          <Grid {...defaultFullGridProps}>
            <Divider />
            <Typography variant="h2">
              NetSuite
            </Typography>
          </Grid>

          <Grid {...defaultHalfGridProps}>
            <ControlledNetSuiteSubsidiarySelectField
              {...defaultTextFieldProps}
              fieldKey="netSuiteSubsidiary"
              label="Subsidiary" />
          </Grid>

          <Grid {...defaultHalfGridProps}>
            <ControlledNetSuiteBrandSelectField
              {...defaultTextFieldProps}
              fieldKey="netSuiteBrand"
              label="Brand" />
          </Grid>

          <Grid {...defaultHalfGridProps}>
            <ControlledNetSuiteLocationSelectField
              {...defaultTextFieldProps}
              fieldKey="netSuiteLocation"
              label="Location" />
          </Grid>

          <Grid {...defaultHalfGridProps}>
            <ControlledNetSuiteDepartmentSelectField
              {...defaultTextFieldProps}
              fieldKey="netSuiteDepartment"
              label="Department" />
          </Grid>

          <Grid {...defaultHalfGridProps}>
            <ControlledSalesChannelSelectField
              {...defaultTextFieldProps}
              fieldKey="salesChannel"
              label="Sales Channel" />
          </Grid>

          <Grid {...defaultHalfGridProps}>
            <ControlledTextField
              {...defaultTextFieldProps}
              isRequired={storeType === 'cleoTier1'}
              fieldKey="netSuiteClientInternalId"
              label="Client NS Internal ID"
              extraValidationRules={{ pattern: validationUtil.reDecimalNumber }} />
          </Grid>

          <Grid {...defaultHalfGridProps}>
            <ControlledNetSuiteTaxItemSelectField
              {...defaultTextFieldProps}
              fieldKey="netSuiteTaxItem"
              label="Tax Item" />
          </Grid>

          {/* <Grid {...defaultFullGridProps}>
            <Divider />
            <Typography variant="h2">E1</Typography>
          </Grid> */}

          <Grid {...defaultFullGridProps}>
            <Divider />

            <Typography variant="h2">Connection</Typography>
          </Grid>

          <Grid {...defaultFullGridProps}>
            <ToggleButtonGroup
              exclusive
              value={storeType}
              onChange={handleChangeStoreType}>
              <ToggleButton value="wooCommerce">Woocommerce</ToggleButton>
              <ToggleButton value="shopify">Shopify</ToggleButton>
              <ToggleButton value="telgoo5">Telgoo5</ToggleButton>
              <ToggleButton value="customizableFormat">Custom CSV</ToggleButton>
              <ToggleButton value="cleoTier1">CLEO T1</ToggleButton>
              <ToggleButton value="magicJack">Magic Jack</ToggleButton>
            </ToggleButtonGroup>
          </Grid>

          <Grid {...defaultFullGridProps}>
            {(storeType === 'shopify' || storeType === 'wooCommerce') && (
              <ControlledTextField
                {...defaultTextFieldProps}
                fieldKey="baseUrl"
                label="Base URL"
                extraValidationRules={{ pattern: validationUtil.reUrl }} />
            )}
            {storeType === 'magicJack' && (
              <ControlledTextField
                {...defaultTextFieldProps}
                fieldKey="sftpHost"
                label="Host" />
            )}
          </Grid>

          {storeType === 'wooCommerce' && (
            <>
              <Grid {...defaultHalfGridProps}>
                <ControlledTextField
                  {...defaultTextFieldProps}
                  fieldKey="wooCommerceKey"
                  label="Key" />
              </Grid>

              <Grid {...defaultHalfGridProps}>
                <ControlledTextField
                  {...defaultTextFieldProps}
                  fieldKey="wooCommerceSecret"
                  label="Secret" />
              </Grid>
            </>)}

          {storeType === 'shopify' && (
            <Grid {...defaultFullGridProps}>
              <ControlledTextField
                {...defaultTextFieldProps}
                fieldKey="shopifyAdminKey"
                label="Admin Key" />
            </Grid>
          )}

          {storeType === 'telgoo5' && (
            <>
              <Grid {...defaultHalfGridProps}>
                <ControlledTextField
                  {...defaultTextFieldProps}
                  fieldKey="telgoo5CustomerId"
                  label="Customer ID" />
              </Grid>

              <Grid {...defaultHalfGridProps}>
                <ControlledTextField
                  {...defaultTextFieldProps}
                  fieldKey="telgoo5PartnerName"
                  label="Partner Name" />
              </Grid>

              <Grid {...defaultHalfGridProps}>
                <ControlledTextField
                  {...defaultTextFieldProps}
                  fieldKey="telgoo5SourceUrl"
                  label="Source URL"
                  extraValidationRules={{ pattern: validationUtil.reUrl }} />
              </Grid>

              <Grid {...defaultHalfGridProps}>
                <ControlledTextField
                  {...defaultTextFieldProps}
                  fieldKey="telgoo5ApiUrl"
                  label="API URL"
                  extraValidationRules={{ pattern: validationUtil.reUrl }} />
              </Grid>

              <Grid {...defaultHalfGridProps}>
                <Stack spacing={1}>
                  {fieldArray.fields.map((field, i) => (
                    <Stack
                      key={field.id}
                      direction="row">
                      <ControlledTextField
                        {...defaultTextFieldProps}
                        fieldKey={`clientIdentifiers.${i}.value`}
                        label="IP Address" />

                      <IconButton onClick={_.partial(handleClickDeleteIpAddress, i)}>
                        <DeleteForever />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>

                <Button onClick={handleClickAddIpAddress}>
                  Add IP Address
                </Button>
              </Grid>
            </>
          )}

          {storeType === 'magicJack' && (
            <>
              <Grid {...defaultHalfGridProps}>
                <ControlledTextField
                  {...defaultTextFieldProps}
                  fieldKey="sftpUsername"
                  label="Username" />
              </Grid>

              <Grid {...defaultHalfGridProps}>
                <ControlledTextField
                  {...defaultTextFieldProps}
                  fieldKey="sftpPassword"
                  label="Password"
                  type="password" />
              </Grid>

              <Grid {...defaultHalfGridProps}>
                <ControlledTextField
                  {...defaultTextFieldProps}
                  fieldKey="sftpOrderFileDirectory"
                  label="Order File Location" />
              </Grid>

              <Grid {...defaultHalfGridProps}>
                <ControlledTextField
                  {...defaultTextFieldProps}
                  fieldKey="sftpAckFileDirectory"
                  label="Acknowledgment File Location" />
              </Grid>

              <Grid {...defaultHalfGridProps}>
                <ControlledTextField
                  {...defaultTextFieldProps}
                  fieldKey="sftpShipFileDirectory"
                  label="Shipment File Location" />
              </Grid>
            </>
          )}

          {storeType === 'cleoTier1' && (
            <>
              <Grid {...defaultFullGridProps}>
                <Divider />
                <Typography variant="h2">Triage Workflows</Typography>
              </Grid>

              <Grid
                {...defaultGridProps}>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}>
                  <FormLabel>
                    Receiving
                  </FormLabel>
                </Stack>
              </Grid>

              <Grid {...defaultHalfGridProps}>
                <ControlledTextField
                  {...defaultTextFieldProps}
                  fieldKey="receivingWorkflow.name"
                  label="Name" />
              </Grid>

              <Grid {...defaultHalfGridProps}>
                <ControlledTextField
                  {...defaultTextFieldProps}
                  extraValidationRules={{ pattern: validationUtil.reDecimalNumber }}
                  fieldKey="receivingWorkflow.binId"
                  label="NS Bin ID" />
              </Grid>

              <Grid {...defaultGridProps}>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}>
                  <FormLabel>
                    Triage
                  </FormLabel>
                </Stack>
              </Grid>

              <Grid {...defaultHalfGridProps}>
                <ControlledTextField
                  {...defaultTextFieldProps}
                  fieldKey="triageWorkflow.name"
                  label="Name" />
              </Grid>

              <Grid {...defaultHalfGridProps}>
                <ControlledTextField
                  {...defaultTextFieldProps}
                  fieldKey="triageWorkflow.binId"
                  label="NS Bin ID"
                  extraValidationRules={{ pattern: validationUtil.reDecimalNumber }} />
              </Grid>

              <Grid {...defaultGridProps}>
                <Divider />
              </Grid>

              <Grid {...defaultGridProps}>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}>
                  <FormLabel>
                    Replace
                  </FormLabel>

                  <Controller
                    control={form.control}
                    name="replacementWorkflow.enabled"
                    render={({ field }) => (
                      <RadioGroup
                        row
                        {...field}>
                        <FormControlLabel
                          value={true}
                          control={<Radio />}
                          label="Yes" />

                        <FormControlLabel
                          value={false}
                          control={<Radio />}
                          label="No" />
                      </RadioGroup>
                    )} />
                </Stack>
              </Grid>

              {formWatch.replacementWorkflow?.enabled === 'true' ? (
                <>
                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      fieldKey="replacementWorkflow.name"
                      label="Name" />
                  </Grid>

                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      fieldKey="replacementWorkflow.binId"
                      label="NS Bin ID" />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid {...defaultGridProps}>
                    <EnableWorkflowBox>
                      Enable Workflow to add bin
                    </EnableWorkflowBox>
                  </Grid>
                </>
              )}

              <Grid {...defaultGridProps}>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}>
                  <FormLabel>
                    Repair
                  </FormLabel>

                  <Controller
                    control={form.control}
                    name="repairWorkflow.enabled"
                    render={({ field }) => (
                      <RadioGroup
                        row
                        {...field}>
                        <FormControlLabel
                          value={true}
                          control={<Radio />}
                          label="Yes" />

                        <FormControlLabel
                          value={false}
                          control={<Radio />}
                          label="No" />
                      </RadioGroup>
                    )} />
                </Stack>
              </Grid>

              {formWatch.repairWorkflow?.enabled === 'true' ? (
                <>
                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      fieldKey="repairWorkflow.name"
                      label="Name" />
                  </Grid>

                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      fieldKey="repairWorkflow.binId"
                      label="NS Bin ID" />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid {...defaultGridProps}>
                    <EnableWorkflowBox>
                      Enable Workflow to add bin
                    </EnableWorkflowBox>
                  </Grid>
                </>
              )}

              <Grid {...defaultGridProps}>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}>
                  <FormLabel>
                    Return
                  </FormLabel>

                  <Controller
                    control={form.control}
                    name="returnWorkflow.enabled"
                    render={({ field }) => (
                      <RadioGroup
                        row
                        {...field}>
                        <FormControlLabel
                          value={true}
                          control={<Radio />}
                          label="Yes" />

                        <FormControlLabel
                          value={false}
                          control={<Radio />}
                          label="No" />
                      </RadioGroup>
                    )} />
                </Stack>
              </Grid>

              {formWatch.returnWorkflow?.enabled === 'true' ? (
                <>
                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      fieldKey="returnWorkflow.name"
                      label="Name" />
                  </Grid>

                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      fieldKey="returnWorkflow.binId"
                      label="NS Bin ID" />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid {...defaultGridProps}>
                    <EnableWorkflowBox>
                      Enable Workflow to add bin
                    </EnableWorkflowBox>
                  </Grid>
                </>
              )}

              <Grid {...defaultGridProps}>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}>
                  <FormLabel>
                    Credit
                  </FormLabel>

                  <Controller
                    control={form.control}
                    name="creditWorkflow.enabled"
                    render={({ field }) => (
                      <RadioGroup
                        row
                        {...field}>
                        <FormControlLabel
                          value={true}
                          control={<Radio />}
                          label="Yes" />

                        <FormControlLabel
                          value={false}
                          control={<Radio />}
                          label="No" />
                      </RadioGroup>
                    )} />
                </Stack>
              </Grid>

              {formWatch.creditWorkflow?.enabled === 'true' ? (
                <>
                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      fieldKey="creditWorkflow.name"
                      label="Name" />
                  </Grid>

                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      fieldKey="creditWorkflow.binId"
                      label="NS Bin ID" />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid {...defaultGridProps}>
                    <EnableWorkflowBox>
                      Enable Workflow to add bin
                    </EnableWorkflowBox>
                  </Grid>
                </>
              )}

              <Grid {...defaultGridProps}>
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}>
                  <FormLabel>
                    Scrap
                  </FormLabel>

                  <Controller
                    control={form.control}
                    name="scrapWorkflow.enabled"
                    render={({ field }) => (
                      <RadioGroup
                        row
                        {...field}>
                        <FormControlLabel
                          value={true}
                          control={<Radio />}
                          label="Yes" />

                        <FormControlLabel
                          value={false}
                          control={<Radio />}
                          label="No" />
                      </RadioGroup>
                    )} />
                </Stack>
              </Grid>

              {formWatch.scrapWorkflow?.enabled === 'true' ? (
                <>
                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      fieldKey="scrapWorkflow.name"
                      label="Name" />
                  </Grid>

                  <Grid {...defaultHalfGridProps}>
                    <ControlledTextField
                      {...defaultTextFieldProps}
                      fieldKey="scrapWorkflow.binId"
                      label="NS Bin ID" />
                  </Grid>
                </>
              ) : (
                <>
                  <Grid {...defaultGridProps}>
                    <EnableWorkflowBox>
                      Enable Workflow to add bin
                    </EnableWorkflowBox>
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>
      </ContainerBox>
    </FormModal.FC>
  );
};
